/**
 * AccountingPanel — connect your own books (self-hosted Bigcapital).
 *
 * Pay apps, payments and sub invoices sync automatically once connected.
 * Backfill pushes existing history; Test verifies the live connection.
 */

import { useState, useEffect, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, CheckCircle2, AlertTriangle } from 'lucide-react'
import BooksReportsPanel from './BooksReportsPanel'
import OperationsReportPanel from './OperationsReportPanel'
import './BooksReportsPanel.css'
import {
  fetchAccountingConnections,
  createAccountingConnection,
  deleteAccountingConnection,
  testAccountingConnection,
  previewAccountingBackfill,
  runAccountingBackfill,
  runAccountingReports,
  type AccountingTestResult,
  type BackfillPreview,
  type BackfillResult,
  fetchBooksReview, fetchBooksAccounts, applyBookTxn, dismissBookTxn, runBooksNow, createBooksAccount,
} from '../../../api/accounting'
import type { AccountingConnection, BookTxn, BooksAccount } from '../../../api/accounting'

function isSentioBookeeping(c: AccountingConnection) {
  return c.provider === 'bookeeping'
    && (c.metadata?.company_label === 'Sentio' || !c.metadata?.company_label)
}

export default function AccountingPanel() {
  const [searchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('acctTab')
  const [acctTab, setAcctTab] = useState<'connection' | 'reports' | 'operations' | 'bookkeeper'>(
    tabFromUrl === 'operations' ? 'operations' : tabFromUrl === 'connection' ? 'connection' : tabFromUrl === 'bookkeeper' ? 'bookkeeper' : 'reports',
  )

  useEffect(() => {
    if (tabFromUrl === 'operations') setAcctTab('operations')
    else if (tabFromUrl === 'connection') setAcctTab('connection')
    else if (tabFromUrl === 'bookkeeper') setAcctTab('bookkeeper')
    else if (tabFromUrl === 'reports') setAcctTab('reports')
  }, [tabFromUrl])
  const qc = useQueryClient()
  const { data: connections = [], isLoading } = useQuery({
    queryKey: ['accounting-connections'],
    queryFn: fetchAccountingConnections,
    staleTime: 15_000,
  })
  const conn = connections.find(c => c.provider === 'bigcapital') ?? null
  const sentioBk = connections.find(isSentioBookeeping) ?? null

  const [baseUrl, setBaseUrl]   = useState('')
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy]         = useState<string | null>(null)
  const [err, setErr]           = useState<string | null>(null)
  const [testResult, setTestResult] = useState<AccountingTestResult | null>(null)
  const [reportsSent, setReportsSent] = useState(false)

  async function handleConnect(e: FormEvent) {
    e.preventDefault()
    setErr(null); setBusy('connect')
    try {
      await createAccountingConnection({ provider: 'bigcapital', base_url: baseUrl.trim(), email: email.trim(), password })
      setPassword('')
      await qc.invalidateQueries({ queryKey: ['accounting-connections'] })
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Connection failed')
    } finally { setBusy(null) }
  }

  async function handleTest() {
    if (!conn) return
    setErr(null); setBusy('test'); setTestResult(null)
    try { setTestResult(await testAccountingConnection(conn.id)) }
    catch (ex) { setErr(ex instanceof Error ? ex.message : 'Test failed') }
    finally { setBusy(null) }
  }

  async function handleReports() {
    setErr(null); setBusy('reports'); setReportsSent(false)
    try { await runAccountingReports(); setReportsSent(true) }
    catch (ex) { setErr(ex instanceof Error ? ex.message : 'Report send failed') }
    finally { setBusy(null) }
  }

  async function handleDisconnect() {
    if (!conn) return
    setErr(null); setBusy('disconnect')
    try { await deleteAccountingConnection(conn.id); await qc.invalidateQueries({ queryKey: ['accounting-connections'] }) }
    catch (ex) { setErr(ex instanceof Error ? ex.message : 'Disconnect failed') }
    finally { setBusy(null) }
  }

  return (
    <section className="set-panel" data-testid="settings-accounting">
      <div className="set-panel-h">
        <BookOpen size={15} />
        <div>
          <div className="set-panel-tt">Accounting</div>
          <div className="set-panel-st">
            Books reports from Bookeeping.ai (Sentio / Varshyl), plus Bigcapital sync and Soren Bookkeeper review.
          </div>
        </div>
      </div>

      <div className="books-seg" style={{ marginBottom: 16, maxWidth: 520 }}>
        {(['connection', 'reports', 'operations', 'bookkeeper'] as const).map(id => (
          <button key={id} type="button"
            className={`books-seg-btn${acctTab === id ? ' on' : ''}`}
            onClick={() => setAcctTab(id)}
            data-testid={`acct-tab-${id}`}>
            {id === 'connection' ? 'Connection' : id === 'reports' ? 'Reports' : id === 'operations' ? 'Operations' : 'Bookkeeper'}
          </button>
        ))}
      </div>

      {acctTab === 'reports' && <BooksReportsPanel />}

      {acctTab === 'operations' && <OperationsReportPanel />}

      {acctTab === 'bookkeeper' && connections.length > 0 && <BooksReview />}

      {acctTab === 'connection' && (
      <>
      {sentioBk && (
        <SentioBillingSync
          connection={sentioBk}
          onError={setErr}
          onConnectionsChange={() => qc.invalidateQueries({ queryKey: ['accounting-connections'] })}
        />
      )}

      {isLoading ? (
        <div className="skel" style={{ width: '100%', height: 60 }} />
      ) : conn ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
            <CheckCircle2 size={14} color="var(--green)" />
            <b>Connected</b>
            <span style={{ color: 'var(--ink4)' }}>· {conn.email} · org {conn.org_id}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 12 }}>
            {conn.base_url}
            {conn.last_sync_at && <> · last sync {new Date(conn.last_sync_at).toLocaleString()}</>}
          </div>
          {conn.last_error && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: 'var(--amber)', marginBottom: 12 }}>
              <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>Last sync error: {conn.last_error}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn b-ol btn-sm" disabled={busy != null} onClick={handleTest}>
              {busy === 'test' ? 'Testing…' : 'Test connection'}
            </button>
            <button className="btn b-ol btn-sm" disabled={busy != null} onClick={handleReports}>
              {busy === 'reports' ? 'Sending…' : 'Email reports now'}
            </button>
            <button className="btn b-ol btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red-l)' }}
              disabled={busy != null} onClick={handleDisconnect}>
              Disconnect
            </button>
          </div>

          {testResult && (
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 12 }}>
              ✓ {testResult.account_count} accounts · income “{testResult.income_account}” ·
              deposits “{testResult.deposit_account}” · costs “{testResult.cost_account}”
            </div>
          )}
          {reportsSent && (
            <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 12 }}>
              Reports for last month are on their way to your inbox.
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleConnect} style={{ maxWidth: 460 }}>
          <label className="fld-l">Bigcapital URL</label>
          <input className="fld-i" placeholder="https://your-bigcapital.example.com" value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)} required data-testid="acct-base-url" />
          <label className="fld-l" style={{ marginTop: 10 }}>Email</label>
          <input className="fld-i" type="email" value={email}
            onChange={e => setEmail(e.target.value)} required data-testid="acct-email" />
          <label className="fld-l" style={{ marginTop: 10 }}>Password</label>
          <input className="fld-i" type="password" value={password}
            onChange={e => setPassword(e.target.value)} required data-testid="acct-password" />
          <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 6 }}>
            Your organization is detected automatically. The password is stored encrypted and only used to talk to your books.
          </div>
          <button className="btn b-dk" style={{ marginTop: 12 }} disabled={busy != null} data-testid="acct-connect">
            {busy === 'connect' ? 'Connecting…' : 'Connect'}
          </button>
        </form>
      )}

      {err && acctTab === 'connection' && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 12 }}>{err}</div>}
      </>
      )}
    </section>
  )
}

function SentioBillingSync({ connection, onError, onConnectionsChange }: {
  connection: AccountingConnection
  onError: (msg: string | null) => void
  onConnectionsChange: () => void
}) {
  const [busy, setBusy] = useState<'preview' | 'run' | null>(null)
  const [preview, setPreview] = useState<BackfillPreview | null>(null)
  const [checked, setChecked] = useState<Record<number, boolean>>({})
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  const [runResult, setRunResult] = useState<BackfillResult | null>(null)

  const selectedIds = preview
    ? preview.projects.filter(p => checked[p.projectId]).map(p => p.projectId)
    : []

  async function handlePreview() {
    onError(null)
    setBusy('preview')
    setRunResult(null)
    setChecked({})
    try {
      const data = await previewAccountingBackfill()
      setPreview(data)
      const init: Record<number, boolean> = {}
      for (const p of data.projects) init[p.projectId] = false
      setChecked(init)
    } catch (ex) {
      onError(ex instanceof Error ? ex.message : 'Preview failed')
    } finally {
      setBusy(null)
    }
  }

  async function handleRun() {
    if (!selectedIds.length) {
      onError('Select at least one project to sync.')
      return
    }
    onError(null)
    setBusy('run')
    try {
      const data = await runAccountingBackfill(selectedIds)
      setRunResult(data)
      onConnectionsChange()
    } catch (ex) {
      onError(ex instanceof Error ? ex.message : 'Sync failed')
    } finally {
      setBusy(null)
    }
  }

  function toggleProject(id: number, on: boolean) {
    setChecked(prev => ({ ...prev, [id]: on }))
  }

  return (
    <div className="acct-sentio-sync" data-testid="sentio-billing-sync">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
        <CheckCircle2 size={14} color="var(--green)" />
        <b>Sentio → Bookeeping.ai billing sync</b>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 12 }}>
        Per-project only — preview, check projects, then run. Test projects are hidden.
        Pay apps land as DRAFT invoices. Varshyl stays read-only.
        {connection.last_sync_at && <> · last sync {new Date(connection.last_sync_at).toLocaleString()}</>}
      </div>
      {connection.last_error && !runResult && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: 'var(--amber)', marginBottom: 12 }}>
          <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>Last sync error: {connection.last_error}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button type="button" className="btn b-ol btn-sm" disabled={busy != null} onClick={handlePreview}
          data-testid="sentio-sync-preview">
          {busy === 'preview' ? 'Previewing…' : 'Preview sync'}
        </button>
        <button type="button" className="btn b-dk btn-sm"
          disabled={busy != null || !preview || selectedIds.length === 0}
          onClick={handleRun} data-testid="sentio-sync-run">
          {busy === 'run' ? 'Running sync…' : `Run sync (${selectedIds.length} selected)`}
        </button>
      </div>

      {preview && (
        <div className="acct-sync-preview">
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            {preview.projectCount} project{preview.projectCount === 1 ? '' : 's'} · {preview.itemCount} items
            {preview.skippedVarshyl > 0 && (
              <span style={{ color: 'var(--ink4)', fontWeight: 400 }}> · Varshyl skipped</span>
            )}
          </div>
          {preview.projects.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--ink4)' }}>No sync-eligible projects (test projects excluded).</div>
          ) : (
            preview.projects.map(proj => (
              <div key={proj.projectId} className="acct-sync-project" data-testid={`sync-project-${proj.projectId}`}>
                <label className="acct-sync-project-h">
                  <input type="checkbox"
                    checked={!!checked[proj.projectId]}
                    onChange={e => toggleProject(proj.projectId, e.target.checked)}
                    data-testid={`sync-check-${proj.projectId}`} />
                  <span>
                    <b>{proj.projectName}</b>
                    {proj.projectNumber && <span style={{ color: 'var(--ink4)' }}> · #{proj.projectNumber}</span>}
                    <span style={{ color: 'var(--ink4)', fontWeight: 400 }}> — {proj.itemCount} items</span>
                  </span>
                  <button type="button" className="acct-sync-expand"
                    onClick={() => setExpanded(e => ({ ...e, [proj.projectId]: !e[proj.projectId] }))}>
                    {expanded[proj.projectId] ? 'Hide' : 'Show'}
                  </button>
                </label>
                {expanded[proj.projectId] && (
                  <ul className="acct-sync-items">
                    {proj.payApps.map(pa => (
                      <li key={`pa-${pa.payAppId}`}>Pay app {pa.invoiceNo} · {pa.status} · ${String(pa.amount ?? '—')}</li>
                    ))}
                    {proj.payments.map(p => (
                      <li key={`mp-${p.paymentId}`}>Payment #{p.paymentId} · PA #{p.payAppId} · ${String(p.amount ?? '—')}</li>
                    ))}
                    {proj.subBills.map(b => (
                      <li key={`sub-${b.uploadId}`}>Sub bill · {b.vendor || 'Vendor'}{b.paid ? ' (paid)' : ''}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {runResult && (
        <div style={{ fontSize: 12, marginTop: 12 }} data-testid="sentio-sync-result">
          <div style={{ color: runResult.errors.length ? 'var(--amber)' : 'var(--green)' }}>
            Sync complete: {runResult.invoices.length} invoices, {runResult.payments.length} payments,{' '}
            {runResult.bills.length} sub bills across {runResult.projectIds.length} project(s).
          </div>
          {runResult.invoices.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
              Invoice IDs: {runResult.invoices.map(x => x.remoteId).slice(0, 8).join(', ')}
              {runResult.invoices.length > 8 ? '…' : ''}
            </div>
          )}
          {runResult.errors.length > 0 && (
            <details style={{ marginTop: 8 }} data-testid="sentio-sync-errors">
              <summary style={{ cursor: 'pointer', color: 'var(--amber)', fontWeight: 600 }}>
                {runResult.errors.length} error{runResult.errors.length === 1 ? '' : 's'} — click to expand
              </summary>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--ink3)', fontSize: 11 }}>
                {runResult.errors.map((err, i) => (
                  <li key={i} style={{ marginBottom: 6, wordBreak: 'break-word' }}>{err}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--line)', margin: '20px 0' }} />
    </div>
  )
}

// ── Soren Bookkeeper review (Phase D) ────────────────────────────────────────

function confColor(c: number): string {
  if (c >= 0.9) return 'var(--green)'
  if (c >= 0.7) return '#eab308'
  return 'var(--red)'
}

function BooksReview() {
  const qc = useQueryClient()
  const review = useQuery({ queryKey: ['books-review'], queryFn: fetchBooksReview })
  const accounts = useQuery({ queryKey: ['books-accounts'], queryFn: fetchBooksAccounts })
  const [fixSel, setFixSel] = useState<Record<number, number>>({})
  const [running, setRunning] = useState(false)

  const apply = useMutation({
    mutationFn: ({ id, accountId }: { id: number; accountId?: number }) => applyBookTxn(id, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['books-review'] }),
  })
  const dismiss = useMutation({
    mutationFn: (id: number) => dismissBookTxn(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['books-review'] }),
  })

  async function runNow() {
    setRunning(true)
    try { await runBooksNow(); await qc.invalidateQueries({ queryKey: ['books-review'] }) }
    finally { setRunning(false) }
  }

  const rows = review.data?.data ?? []
  const pendingAll = rows.filter(r => r.action === 'pending' || r.action === 'error')
  // group by merchant + direction: one decision covers all repeats
  const groups = new Map<string, typeof pendingAll>()
  for (const r of pendingAll) {
    const key = `${r.merchant_key ?? r.id}|${parseFloat(r.amount) >= 0 ? '+' : '-'}`
    const g = groups.get(key) ?? []
    g.push(r); groups.set(key, g)
  }
  const pending = [...groups.values()].map(g => ({ ...g[0], _count: g.length,
    _total: g.reduce((s, r) => s + Math.abs(parseFloat(r.amount)), 0) })) as (BookTxn & { _count: number; _total: number })[]
  const recent = rows.filter(r => r.action === 'matched' || r.action === 'auto').slice(0, 10)
  const counts = review.data?.counts ?? {}
  const money = (n: string) => {
    const v = parseFloat(n)
    return `${v >= 0 ? '+' : '−'}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  return (
    <div style={{ marginTop: 26, borderTop: '1px solid var(--line)', paddingTop: 18 }} data-testid="books-review">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Soren Bookkeeper</div>
          <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>
            Bank transactions matched to your books, categorized by AI. Runs Wednesday and Saturday; digest lands Friday.
            {' '}{counts.matched ? `${counts.matched} reconciled · ` : ''}{counts.auto ? `${counts.auto} auto-categorized · ` : ''}{pending.length} to review
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn b-ol btn-sm" onClick={async () => {
            const name = window.prompt('New category name (e.g. "Print & Reprographics", "Subcontractor Checks"):')
            if (!name?.trim()) return
            const kind = window.prompt('Type: expense, income, or cost-of-goods-sold', 'expense') || 'expense'
            await createBooksAccount(name.trim(), kind.trim())
            await qc.invalidateQueries({ queryKey: ['books-accounts'] })
          }}>+ New category</button>
          <button className="btn b-ol btn-sm" onClick={runNow} disabled={running} data-testid="books-run-now">
            {running ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>

      {pending.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {pending.map(t => {
            const conf = t.confidence != null ? parseFloat(t.confidence) : 0
            return (
              <div key={t.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--line)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {t.description || t.payee || '—'}
                    {t._count > 1 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: 'var(--ink3)', background: 'var(--line)', borderRadius: 8, padding: '1px 7px' }}>×{t._count}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink4)' }}>
                    {String(t.txn_date).slice(0, 10)} · {t.bank_account_name ?? ''} · {t._count > 1
                      ? `${parseFloat(t.amount) >= 0 ? '+' : '−'}$${t._total.toLocaleString('en-US', { minimumFractionDigits: 2 })} across ${t._count}`
                      : money(t.amount)}
                  </div>
                  {t.reason && <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2 }}>{t.reason}</div>}
                </div>
                <div style={{ width: 130 }}>
                  <div style={{ fontSize: 11, color: confColor(conf), fontWeight: 700 }}>
                    {Math.round(conf * 100)}% confident
                  </div>
                  <div style={{ height: 4, background: 'var(--line)', borderRadius: 2, marginTop: 3 }}>
                    <div style={{ height: 4, width: `${Math.round(conf * 100)}%`, background: confColor(conf), borderRadius: 2 }} />
                  </div>
                </div>
                <select className="fld-i" style={{ width: 200, fontSize: 12 }}
                  value={fixSel[t.id] ?? t.suggested_account_id ?? ''}
                  onChange={e => setFixSel(m => ({ ...m, [t.id]: parseInt(e.target.value, 10) }))}>
                  <option value="" disabled>Pick account…</option>
                  {(accounts.data ?? []).map((a: BooksAccount) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                <button className="btn b-dk btn-sm"
                  disabled={apply.isPending || !(fixSel[t.id] ?? t.suggested_account_id)}
                  onClick={() => apply.mutate({ id: t.id, accountId: fixSel[t.id] ?? t.suggested_account_id ?? undefined })}>
                  {t._count > 1 ? `Apply to all ${t._count}` : 'Apply'}
                </button>
                <button className="btn b-ol btn-sm" disabled={dismiss.isPending} onClick={() => dismiss.mutate(t.id)}>
                  Skip
                </button>
              </div>
            )
          })}
        </div>
      )}

      {pending.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 10 }}>
          Nothing awaiting review. {rows.length === 0 ? 'Press "Run now" after connecting your bank in Bigcapital.' : ''}
        </div>
      )}

      {recent.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 12, color: 'var(--ink3)', cursor: 'pointer' }}>Recent Soren activity ({recent.length})</summary>
          {recent.map(t => (
            <div key={t.id} style={{ fontSize: 12, color: 'var(--ink3)', padding: '4px 0' }}>
              {String(t.txn_date).slice(0, 10)} · {(t.description || '—').slice(0, 44)} · {money(t.amount)} → {t.matched_reference || t.suggested_account_name}
              {' '}<span style={{ color: 'var(--ink4)' }}>({t.action}{t.confidence ? `, ${Math.round(parseFloat(t.confidence) * 100)}%` : ''})</span>
            </div>
          ))}
        </details>
      )}
    </div>
  )
}
